import { useState, useCallback, useMemo } from 'react';
import {
  Chat,
  Message,
  ChatSendMessageEvent,
  ChatMessageTemplateProps,
  User,
} from '@progress/kendo-react-conversational-ui';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import './KendoEquipmentTable.css';
import {
  Assembly,
  useGetStructureNameQuery,
  useKendoAiChatMutation,
} from '@/__generated__/graphql';
import { useAtomValue, useSetAtom } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import * as state from '@/components/Analysis/state';

const BOT: User = { id: 0, name: 'bot' };
const USER_AUTHOR: User = { id: 1, name: 'user' };

const DEFAULT_SUGGESTIONS = [
  { id: 'sort-component-asc', text: 'Sort by Component ascending' },
  { id: 'sort-component-desc', text: 'Sort by Component descending' },
  { id: 'show-all-columns', text: 'Show all columns' },
  { id: 'hide-all-columns', text: 'Hide all columns' },
  { id: 'reset-all', text: 'Reset all filters and sorting' },
];

const createMessage = (text: string, author: User, id?: string): Message => ({
  id: id || `msg-${Date.now()}`,
  author,
  timestamp: new Date(),
  text,
});

const AIMessageTemplate: React.FC<ChatMessageTemplateProps> = ({ item }) => (
  <Typography sx={{ py: 2, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
    {item?.text}
  </Typography>
);

/**
 * Parse templateFieldData from JSON string to object.
 * The server returns templateFieldData as a stringified JSON to maintain data shape.
 */
type TemplateFilter = { id: string; value: string[] };
type ToolResult = { name: string; result?: string; error?: string };

const parseAssemblyTemplateData = (assembly: Assembly | null | undefined): Assembly | null => {
  if (!assembly) return null;
  const parsed = { ...(assembly as unknown as Record<string, unknown>) } as Assembly;
  if (typeof parsed.templateFieldData === 'string') {
    try {
      (parsed as any).templateFieldData = JSON.parse(parsed.templateFieldData as unknown as string);
    } catch {
      (parsed as any).templateFieldData = {};
    }
  } else if (!parsed.templateFieldData) {
    (parsed as any).templateFieldData = {};
  }
  return parsed;
};

/**
 * Strip __typename from filters (added by Apollo Client for caching).
 * GraphQL schema doesn't accept this field in FiltersInput.
 */
const cleanFilters = (
  filters: Array<{ id: string; value: string[]; __typename?: string }>
): Array<{ id: string; value: string[] }> => {
  return filters.map(({ id, value }) => ({ id, value }));
};

export interface AIChatProps {
  onClose?: () => void;
  data: Assembly[];
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onShowAllColumns?: () => void;
  onHideAllColumns?: () => void;
  onHideColumn?: (columnName: string) => void;
  onReset?: () => void;
  onUpdateAssemblies?: (assemblies: Assembly[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onAppliedFiltersChange?: (filters: Array<{ id: string; value: string[] }>) => void;
  selectedTemplateFilters?: Array<{ id: string; value: string[] }>;
  pageInfo?: { skip: number; limit: number; sortBy: string; sortDir: number };
}

export const AIChat: React.FC<AIChatProps> = ({
  onClose,
  data,
  onSort,
  onShowAllColumns,
  onHideAllColumns,
  onReset,
  onUpdateAssemblies,
  onLoadingChange,
  onAppliedFiltersChange,
  selectedTemplateFilters,
  pageInfo,
}) => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const allTemplates = useAtomValue(state.allAssemblyTemplates);
  const setInspectionPlans = useSetAtom(state.inspectionPlansState);
  const setSelectedInspectionPlanId = useSetAtom(state.selectedInspectionPlanId);
  const selectedDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);

  const setSelectedDeckIds = useSetAtom(state.selectedFilteredDeckIds);
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);
  const setSelectedWorkpackIds = useSetAtom(state.selectedFilteredWorkpackIds);

  const { data: structureNameData } = useGetStructureNameQuery({
    variables: { structureId: structureId ?? '' },
    fetchPolicy: 'cache-first',
  });

  const [kendoAiChat] = useKendoAiChatMutation();
  const [messages, setMessages] = useState<Message[]>([
    createMessage(
      `👋 Hi! I'm Planner AI, your Grid Assistant. I'm currently viewing ${data.length} components. I can help you with:\n\n• Sort by any field (e.g., "Sort by Component ascending")\n• Filter data\n• Hide/Show columns\n\nStart with one of the suggestions below or type your own request.`,
      BOT,
      '1'
    ),
  ]);
  const [chatHistory, setChatHistory] = useState<{ role: string; content: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Track applied filters from server responses - needed for plan creation on next request
  const [appliedFiltersFromServer, setAppliedFiltersFromServer] = useState<
    Array<{ id: string; value: string[] }>
  >([]);

  const assemblyFilterKeys = useMemo(
    () =>
      allTemplates
        ?.find((template) => template.id === selectedAssemblyTemplateId)
        ?.fields.map((field) => ({
          name: field.name,
          key: field.key,
          type: field.type,
          ...(field.options?.length ? { options: field.options } : {}),
        })) ?? [],
    [allTemplates, selectedAssemblyTemplateId]
  );

  const sendMessage = useCallback(
    async (messageText: string) => {
      setMessages((prev) => [...prev, createMessage(messageText, USER_AUTHOR)]);

      const newHistory = [...chatHistory, { role: 'user', content: messageText }];
      setChatHistory(newHistory);
      setIsLoading(true);
      onLoadingChange?.(true);

      try {
        const filtersToSend =
          appliedFiltersFromServer.length > 0
            ? cleanFilters(appliedFiltersFromServer)
            : cleanFilters(selectedTemplateFilters ?? []);

        const { data: responseData } = await kendoAiChat({
          variables: {
            input: {
              messages: newHistory,
              structure: {
                id: structureId ?? '',
                name: structureNameData?.structure?.name ?? 'N/A',
              },
              assemblyTemplateId: selectedAssemblyTemplateId ?? '',
              // Use filters from previous server response, fallback to prop
              selectedTemplateFilters: filtersToSend,
              // Spatial filters: decks, paint regions, workpacks
              selectedDeckIds: selectedDeckIds ?? [],
              selectedPaintRegionIds: selectedPaintRegionIds ?? [],
              selectedWorkpackIds: selectedWorkpackIds ?? [],
              pageInfo,
              assemblyFilterKeys: assemblyFilterKeys.map((field) => ({
                name: field.name,
                key: field.key,
                options: field.options,
              })),
            },
          },
        });

        const chatResponse = responseData?.kendoAIChat;
        console.log('🚀 ~ AIChat ~ chatResponse:', chatResponse);
        const chatResponseAny = chatResponse as any;
        if (!chatResponse?.success) {
          setMessages((prev) => [
            ...prev,
            createMessage(chatResponse?.error || 'An error occurred.', BOT),
          ]);
          return;
        }

        const aiText = chatResponse.response?.text || '';
        if (aiText) {
          setMessages((prev) => [...prev, createMessage(aiText, BOT)]);
          setChatHistory((prev) => [...prev, { role: 'assistant', content: aiText }]);
        }
        // If the server returned top-level selected filters or assemblies, use them first
        if (chatResponse.selectedTemplateFilters && chatResponse.selectedTemplateFilters.length) {
          const serverFilters = chatResponse.selectedTemplateFilters as TemplateFilter[];
          onAppliedFiltersChange?.(serverFilters);
          // Store filters locally for next request
          setAppliedFiltersFromServer(serverFilters);
        }

        // Apply spatial filters returned from the server (decks / paint regions / workpacks)
        if (chatResponseAny.selectedDeckIds && chatResponseAny.selectedDeckIds.length) {
          setSelectedDeckIds(chatResponseAny.selectedDeckIds as string[]);
        }
        if (
          chatResponseAny.selectedPaintRegionIds &&
          chatResponseAny.selectedPaintRegionIds.length
        ) {
          setSelectedPaintRegionIds(chatResponseAny.selectedPaintRegionIds as string[]);
        }
        if (chatResponseAny.selectedWorkpackIds && chatResponseAny.selectedWorkpackIds.length) {
          setSelectedWorkpackIds(chatResponseAny.selectedWorkpackIds as string[]);
        }

        if (chatResponse.assemblies && Array.isArray(chatResponse.assemblies.data)) {
          const parsedAssemblies = (chatResponse.assemblies.data as Assembly[])
            .map(parseAssemblyTemplateData)
            .filter(Boolean) as Assembly[];
          onUpdateAssemblies?.(parsedAssemblies);
        }

        if (chatResponse.inspectionPlan) {
          const createdPlan = chatResponse.inspectionPlan;
          setInspectionPlans((previous) => {
            const withoutExisting = previous.filter((plan) => plan?.id !== createdPlan.id);
            return [createdPlan, ...withoutExisting];
          });
          setSelectedInspectionPlanId(createdPlan.id);
        }

        // Fallback: Process tool results (for older server responses)
        const toolResults: ToolResult[] = (chatResponse.toolResults as ToolResult[]) ?? [];
        let hasErrors = false;

        if (toolResults.length) {
          const errorTools: Array<{ name: string; error: string }> = [];
          toolResults.forEach((tool) => {
            if (tool.error) {
              console.error(`Tool ${tool.name} error:`, tool.error);
              errorTools.push({ name: tool.name, error: tool.error });
              hasErrors = true;
              return;
            }
            if (!tool.result) {
              return;
            }

            let result: unknown;
            try {
              result = JSON.parse(tool.result as string);
            } catch (e) {
              console.error(`Failed to parse tool.result for ${tool.name}:`, e);
              return;
            }

            if (tool.name === 'getAssemblies' && result && Array.isArray((result as any).data)) {
              const parsedAssemblies = (result as any).data
                .map(parseAssemblyTemplateData)
                .filter(Boolean) as Assembly[];
              onUpdateAssemblies?.(parsedAssemblies);

              // Pass applied filters back to parent component
              if ((result as any).appliedFilters && Array.isArray((result as any).appliedFilters)) {
                const parsedFilters = (result as any).appliedFilters as TemplateFilter[];
                onAppliedFiltersChange?.(parsedFilters);
                // Store filters locally for next request
                setAppliedFiltersFromServer(parsedFilters);
              }

              // Apply spatial filters returned from tool result if present
              if (
                (result as any).selectedDeckIds &&
                Array.isArray((result as any).selectedDeckIds)
              ) {
                setSelectedDeckIds((result as any).selectedDeckIds as string[]);
              }
              if (
                (result as any).selectedPaintRegionIds &&
                Array.isArray((result as any).selectedPaintRegionIds)
              ) {
                setSelectedPaintRegionIds((result as any).selectedPaintRegionIds as string[]);
              }
              if (
                (result as any).selectedWorkpackIds &&
                Array.isArray((result as any).selectedWorkpackIds)
              ) {
                setSelectedWorkpackIds((result as any).selectedWorkpackIds as string[]);
              }
            }
          });

          // Show error messages if any tool failed
          if (hasErrors) {
            const errorMsg = errorTools.map((t) => `❌ ${t.name}: ${t.error}`).join('\n\n');
            setMessages((prev) => [...prev, createMessage(errorMsg, BOT)]);
          } else if (!aiText) {
            setMessages((prev) => [
              ...prev,
              createMessage(`✓ Executed: ${toolResults.map((t) => t.name).join(', ')}`, BOT),
            ]);
          }
        }
      } catch (error) {
        console.error('Chat error:', error);
        setMessages((prev) => [
          ...prev,
          createMessage('An error occurred. Please try again.', BOT),
        ]);
      } finally {
        setIsLoading(false);
        onLoadingChange?.(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chatHistory,
      kendoAiChat,
      structureId,
      structureNameData,
      selectedAssemblyTemplateId,
      appliedFiltersFromServer,
      selectedTemplateFilters,
      pageInfo,
      assemblyFilterKeys,
      onLoadingChange,
      onUpdateAssemblies,
      onAppliedFiltersChange,
    ]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: any) => {
      const suggestionActions: Record<string, () => void> = {
        'sort-component-asc': () => onSort?.('Component', 'asc'),
        'sort-component-desc': () => onSort?.('Component', 'desc'),
        'show-all-columns': () => onShowAllColumns?.(),
        'hide-all-columns': () => onHideAllColumns?.(),
        'reset-all': () => onReset?.(),
      };
      if (suggestionActions[suggestion.id]) {
        suggestionActions[suggestion.id]();
      } else {
        sendMessage(suggestion.text);
      }
    },
    [onSort, onShowAllColumns, onHideAllColumns, onReset, sendMessage]
  );

  return (
    <Box className="ai-chat-wrapper">
      <Chat
        messages={messages}
        authorId={USER_AUTHOR.id}
        onSendMessage={(event: ChatSendMessageEvent) => {
          const text = event.message?.text?.trim();
          if (text) sendMessage(text);
        }}
        placeholder={isLoading ? 'Thinking...' : 'Ask me to sort, filter, or analyze your data.'}
        suggestions={DEFAULT_SUGGESTIONS}
        onSuggestionClick={handleSuggestionClick}
        suggestionsLayout="scrollbuttons"
        showUsername={false}
        uploadConfig={false}
        headerTemplate={() => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: '100%', pt: '1.6rem' }}
          >
            <AutoAwesomeIcon />
            <Typography sx={{ fontWeight: 600 }}>Planner AI</Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        )}
        receiverMessageSettings={{ messageTemplate: AIMessageTemplate, messageWidthMode: 'full' }}
        style={{ height: '600px' }}
      />
    </Box>
  );
};

export default AIChat;
